import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api, onSessionRejected, setSession } from '@/lib/api';
import { APPLICANT_SESSION_KEY } from '@/lib/applicant/storage';
import {
  completeEntraRedirect,
  directoryRedirectProblem,
  endEntraSession,
  IS_ENTRA_CONFIGURED,
  isDirectoryPopup,
  unreadDirectoryResponse,
} from '@/lib/entra';
import { AuthContext, type AuthState } from './AuthContext';
import type { Me, SessionResponse } from './types';

/**
 * Where the session is remembered.
 *
 * `sessionStorage`, not `localStorage`: an internal tool left signed in on a shared
 * desk is a worse failure than signing in again after closing the tab.
 */
export const SESSION_STORAGE_KEY = 'remittance-intake.session';

/**
 * Where a refusal waits when there is no sign-in page on screen to show it.
 *
 * A redirect sign-in is completed here, not on the sign-in page, so a refusal —
 * somebody Microsoft knows and this application has not been told about — arrives
 * with nothing mounted to report it. It is kept here and read once by the sign-in
 * page, which is where somebody being told "you have not been given access"
 * expects to be.
 */
export const REDIRECT_ERROR_KEY = 'remittance-intake.redirect-error';

function stored(): string | null {
  try {
    return sessionStorage.getItem(SESSION_STORAGE_KEY);
  } catch {
    return null;
  }
}

function remember(token: string | null): void {
  setSession(token);
  try {
    if (token) sessionStorage.setItem(SESSION_STORAGE_KEY, token);
    else sessionStorage.removeItem(SESSION_STORAGE_KEY);
  } catch {
    // Storage can be unavailable (private mode, blocked). The session still works
    // for this page; it just will not survive a reload.
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me | null>(null);
  // Whether the redirect completion has already been started. See the effect.
  const completing = useRef(false);
  // Something to wait for: a stored session to check, or, where Microsoft sign-in
  // is configured, a redirect that may be on its way back. Reporting "signed out"
  // first would bounce somebody off a protected page in the middle of signing in.
  // Inside MSAL's popup there is nothing to wait for; see the effect.
  const [loading, setLoading] = useState(
    () => stored() !== null || (IS_ENTRA_CONFIGURED && !isDirectoryPopup()),
  );

  // Local only: for a session the server has already refused.
  const forget = useCallback(() => {
    remember(null);
    setMe(null);
    void endEntraSession().catch(() => undefined);
  }, []);

  // A person signing out ends their sessions on every device, not only this tab's.
  const signOut = useCallback(() => {
    api.end('/api/auth/sign-out');
    forget();
  }, [forget]);

  useEffect(() => onSessionRejected(forget), [forget]);

  const signIn = useCallback(async (directoryToken: string) => {
    const session = await api.post<SessionResponse>('/api/auth/sign-in', { directoryToken });
    // One session per tab: a member of staff signing in ends any applicant session
    // here, which ApplicantSessionProvider notices and drops.
    try {
      sessionStorage.removeItem(APPLICANT_SESSION_KEY);
    } catch {
      // Storage unavailable; there was nothing stored to clear.
    }
    remember(session.token);
    setMe(session.me);
  }, []);

  useEffect(() => {
    const token = stored();
    if (token !== null) {
      setSession(token);
      // Who they are comes from /api/auth/me, not from the token's claims: the
      // role lives in the database and may have changed since the token was issued.
      api
        .get<Me>('/api/auth/me')
        .then(setMe)
        .catch(() => remember(null))
        .finally(() => setLoading(false));
      return;
    }
    if (!IS_ENTRA_CONFIGURED) return;

    // Inside MSAL's own popup this application is a bystander. Completing the
    // sign-in here cannot work — the request is in the opener's storage — and
    // navigating away on the failure destroys the response the opener is reading.
    if (isDirectoryPopup()) return;

    // Started once per provider, not once per effect run. StrictMode runs effects
    // twice; cancelling the first run discarded the token the directory had
    // already handed over, since it only hands it over once. A ref survives.
    if (completing.current) return;
    completing.current = true;

    // MSAL falls back to a full-page redirect when a popup is blocked, and
    // Microsoft returns the browser to the registered URI: the origin, which is
    // the landing page and knows nothing about signing in. So the redirect is
    // completed here, at the level that owns the session, wherever it lands.
    void completeEntraRedirect()
      .then(async (directoryToken) => {
        if (directoryToken === null) {
          // Microsoft may still have answered and been ignored. Landing silently on
          // the public page with a code in the address bar is the failure this
          // area kept producing; say it instead.
          const reason = directoryRedirectProblem() ?? unreadDirectoryResponse();
          if (reason !== null) {
            sessionStorage.setItem(REDIRECT_ERROR_KEY, reason);
            window.location.replace('/signin');
          }
          return;
        }
        try {
          await signIn(directoryToken);
        } catch (caught) {
          sessionStorage.setItem(
            REDIRECT_ERROR_KEY,
            caught instanceof Error ? caught.message : 'Microsoft sign-in could not be completed.',
          );
          window.location.replace('/signin');
          return;
        }
        if (window.location.pathname === '/' || window.location.pathname === '/signin') {
          window.location.replace('/home');
        }
      })
      .catch(() => undefined)
      .finally(() => setLoading(false));
  }, [signIn]);

  const value = useMemo<AuthState>(
    () => ({ me, loading, signIn, signOut }),
    [me, loading, signIn, signOut],
  );

  return <AuthContext value={value}>{children}</AuthContext>;
}
